import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import {
    getFullWithdrawalDescription,
    useGoalAccount,
    useOnetimeTransactionSourceBalance,
    usePendingWithdrawalAmount,
} from '@common/react/goals';
import { getProcessingDuration } from '@common/react/transactions';
import { dt } from '@common/util/dateTime';
import { useState } from 'react';
import { FrameRenderer } from '../common/SlideAnimator';
import TransactionAmountCard from './TransactionAmountCard';
import TransactionCompleteCard from './TransactionCompleteCard';
import TransactionConfirmationCard from './TransactionConfirmationCard';
import TransactionDepositoryAccountCard from './TransactionDepositoryAccountCard';
import TransactionSlideAnimator from './TransactionSlideAnimator';

interface OnetimeGoalWithdrawalFlowProps {
    sourceId: string;
    goalId: string;
}

const OnetimeGoalWithdrawalFlow = ({
    sourceId,
    goalId,
}: OnetimeGoalWithdrawalFlowProps) => {
    useTrackModalShown('One-Time Goal Withdrawal Flow');
    const goalDirection = 'WITHDRAWAL';
    const isCappedAtAvailableBalance = true;
    const modal = useModal();
    const [destinationId, setDestinationId] = useState<string>();
    const [amount, setAmount] = useState(0);
    const { sourceBalance } = useOnetimeTransactionSourceBalance(sourceId);
    const { pendingWithdrawalAmount } = usePendingWithdrawalAmount(sourceId);
    const sourceGoalAccount = useGoalAccount(sourceId);

    if (!sourceGoalAccount) {
        // This should never happen
        console.warn(
            'Failed to find goal account in OnetimeGoalWithdrawalFlow'
        );
        modal.remove();
        return <></>;
    }

    const fullWithdrawalDescription = getFullWithdrawalDescription({
        amount,
        sourceBalance,
        sourceGoalAccountInvestmentType: sourceGoalAccount.info.investmentType,
        pendingWithdrawalAmount,
    });

    const onClose = () => {
        modal.remove();
    };

    const amountFrame: FrameRenderer = {
        id: 'Amount',
        render: ({ goForward }) => {
            if (!sourceId) return <></>;

            const { min, max } = getProcessingDuration(goalDirection);

            return (
                <TransactionAmountCard
                    title="How much do you want to withdraw?"
                    subtitle={`It usually takes about ${min} to ${max} days to complete.`}
                    sourceId={sourceId}
                    amount={amount}
                    onChangeAmount={setAmount}
                    onNext={goForward}
                    onClose={onClose}
                    pendingWithdrawalAmount={pendingWithdrawalAmount}
                    isCappedAtAvailableBalance={isCappedAtAvailableBalance}
                    fullWithdrawalDescription={fullWithdrawalDescription}
                />
            );
        },
    };

    const depositoryAccountFrame: FrameRenderer = {
        id: 'DepositoryAccount',
        render: ({ goBack, goForward }) => {
            return (
                <TransactionDepositoryAccountCard
                    title="Choose destination account"
                    subtitle="Select an account to transfer to."
                    goalId={goalId}
                    depositoryAccountId={destinationId}
                    onChangeDepositoryAccountId={setDestinationId}
                    onBack={goBack}
                    onNext={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const confirmationFrame: FrameRenderer = {
        id: 'Confirmation',
        render: ({ goBack, goForward }) => {
            if (!destinationId) return <></>;

            return (
                <TransactionConfirmationCard
                    title={'Please review your transfer details'}
                    nextButtonText={'Initiate withdrawal'}
                    sourceId={sourceId}
                    destinationId={destinationId}
                    amount={amount}
                    fullWithdrawalDescription={fullWithdrawalDescription}
                    goalDirection={goalDirection}
                    goalId={goalId}
                    pendingWithdrawalAmount={pendingWithdrawalAmount}
                    // TODO: Consider exposing date scheduling for withdrawals
                    // to members. For now, we'll just use the current date.
                    date={dt().toDateStr()}
                    onBack={goBack}
                    onCompleted={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const completeFrame: FrameRenderer = {
        id: 'Complete',
        render: () => {
            return (
                <TransactionCompleteCard
                    title="Withdrawal initiated!"
                    onClose={onClose}
                />
            );
        },
    };

    const frames: FrameRenderer[] = [
        amountFrame,
        depositoryAccountFrame,
        confirmationFrame,
        completeFrame,
    ];

    return (
        <Modal size="md" isOpen={modal.visible} onClose={onClose} unstyled>
            <TransactionSlideAnimator frames={frames} />
        </Modal>
    );
};

const OnetimeGoalWithdrawalFlowModal =
    ModalManager.create<OnetimeGoalWithdrawalFlowProps>(
        OnetimeGoalWithdrawalFlow
    );

export const showOnetimeGoalWithdrawalFlowModal = (
    props: OnetimeGoalWithdrawalFlowProps
) => ModalManager.show(OnetimeGoalWithdrawalFlowModal, props);
